import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { EmailAccount } from './useData';
import ShareAccountButton from './components/ShareAccountButton';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { ThemeColors } from '../../contexts/ThemeContext';
import api from '../../services/api';
import { mutate as swrMutate } from 'swr';
import { SentFolderIdentitiesSection } from './components/SentFolderIdentitiesSection';
import OAuthConnectSection from './components/OAuthConnectSection';

/** Form-only value; persisted as incomingProtocol=imap + oauthProvider on the account. */
export type IncomingProtocolFormValue = 'imap' | 'pop3' | 'oauth2';

function formIncomingProtocol(formData?: Record<string, unknown>): IncomingProtocolFormValue {
  const p = formData?.incomingProtocol;
  if (p === 'pop3' || p === 'oauth2') return p;
  return 'imap';
}

function isOAuth2FormProtocol(formData?: Record<string, unknown>): boolean {
  return formIncomingProtocol(formData) === 'oauth2';
}

function isImapFormProtocol(formData?: Record<string, unknown>): boolean {
  return formIncomingProtocol(formData) === 'imap';
}

function isPop3FormProtocol(formData?: Record<string, unknown>): boolean {
  return formIncomingProtocol(formData) === 'pop3';
}

function toPersistedIncomingProtocol(
  value: unknown,
): 'imap' | 'pop3' {
  return value === 'pop3' ? 'pop3' : 'imap';
}

function toFormIncomingProtocol(item: EmailAccount): IncomingProtocolFormValue {
  if (item.oauthProvider) return 'oauth2';
  return item.incomingProtocol === 'pop3' ? 'pop3' : 'imap';
}

// Form data type that includes plaintext password fields used in the form
export type EmailAccountFormData = Omit<
  EmailAccount,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'lastSyncAt'
  | 'syncStatus'
  | 'syncError'
  | 'createdBy'
  | '_count'
  | 'incomingProtocol'
> & {
  id?: number;
  password?: string;
  imapPassword?: string;
  pop3Password?: string;
  incomingProtocol?: IncomingProtocolFormValue;
};

/** Values accepted by prepareEmailAccountPayload (wizard form + optional read-only account fields). */
export type EmailAccountPayloadInput = Partial<EmailAccountFormData> &
  Partial<
    Pick<
      EmailAccount,
      | 'sentSourceAccountIds'
      | 'createdAt'
      | 'updatedAt'
      | 'createdBy'
      | '_count'
      | 'lastSyncAt'
      | 'syncStatus'
      | 'syncError'
    >
  > & {
    imapPassword?: string | null;
    pop3Password?: string | null;
    _sentFolderIdentitiesUi?: unknown;
    _oauthConnectUi?: unknown;
  };

export const moduleConfig = {
  module: {
    title: 'Email Accounts',
    description: '',
    i18nKey: 'email-accounts',
    permissionModuleKey: 'email_accounts',
    route: {
      list: '/email-accounts',
      new: '/email-accounts/new',
      detail: (id: number) => `/email-accounts/${id}`,
      edit: (id: number) => `/email-accounts/${id}/edit`,
    },
    api: {
      list: '/email-accounts/manage',
      detail: (id: number) => `/email-accounts/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search email accounts by email address or display name...',
  },
  toolbar: {
    actions: [],
  },
  filters: {
    fields: [],
    applyFilter: (items: EmailAccount[]) => items,
  },
  fields: [
    {
      name: 'emailAddress',
      label: 'Email Address',
      type: 'email',
      required: true,
      placeholder: 'Enter email address',
      autoComplete: 'off',
      validation: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    {
      name: 'displayName',
      label: 'Display Name',
      type: 'text',
      placeholder: 'Enter display name',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'incomingProtocol',
      label: 'Incoming Protocol',
      type: 'select',
      required: true,
      options: [
        { value: 'imap', label: 'IMAP (recommended)' },
        { value: 'oauth2', label: 'OAuth2 — Google / Microsoft (XOAUTH2)' },
        { value: 'pop3', label: 'POP3' },
      ],
      defaultValue: 'imap',
      helperText:
        'IMAP or OAuth2 keep folders in sync; POP3 downloads only. OAuth2 uses IMAP/SMTP with Google or Microsoft sign-in.',
      onChangeSideEffect: (value, _setField, setMultiple) => {
        if (value === 'oauth2') {
          setMultiple({
            imapHost: 'imap.gmail.com',
            imapPort: 993,
            imapSecure: true,
            smtpHost: 'smtp.gmail.com',
            smtpPort: 587,
            smtpSecure: true,
            imapMirrorMode: 'full_mailbox',
          });
        }
      },
    },
    {
      name: 'imapHost',
      label: 'IMAP Host',
      type: 'text',
      required: true,
      placeholder: 'imap.gmail.com',
      validation: {
        required: true,
        maxLength: 255,
      },
    },
    {
      name: 'imapPort',
      label: 'IMAP Port',
      type: 'number',
      required: true,
      placeholder: '993',
      validation: {
        required: true,
        min: 1,
        max: 65535,
      },
    },
    {
      name: 'imapSecure',
      label: 'Use SSL/TLS for IMAP',
      type: 'switch',
      switchLabel: 'Use SSL/TLS',
      description: 'Enable secure connection for IMAP',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'imapUsername',
      label: 'IMAP Username',
      type: 'text',
      placeholder: 'Leave empty to use default username',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'imapPassword',
      label: 'IMAP Password',
      type: 'password',
      placeholder: 'Leave empty to use default password',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'pop3Host',
      label: 'POP3 Host',
      type: 'text',
      placeholder: 'pop.gmail.com',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'pop3Port',
      label: 'POP3 Port',
      type: 'number',
      placeholder: '995',
      validation: {
        min: 1,
        max: 65535,
      },
      defaultValue: 995,
    },
    {
      name: 'pop3Secure',
      label: 'Use SSL/TLS for POP3',
      type: 'switch',
      switchLabel: 'Use SSL/TLS',
      description: 'Enable secure connection for POP3',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'pop3Username',
      label: 'POP3 Username',
      type: 'text',
      placeholder: 'Leave empty to use default username',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'pop3Password',
      label: 'POP3 Password',
      type: 'password',
      placeholder: 'Leave empty to use default password',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'pop3DeleteAfterDownload',
      label: '',
      type: 'switch',
      switchLabel: 'Delete from server after download',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'pop3RetentionDays',
      label: 'Retention on server (days)',
      type: 'number',
      placeholder: '30',
      helperText: 'Keep emails on the server for this many days before deletion. Set 0 to delete immediately after download.',
      validation: {
        min: 0,
        max: 3650,
      },
    },
    {
      name: 'smtpHost',
      label: 'SMTP Host',
      type: 'text',
      required: true,
      placeholder: 'smtp.gmail.com',
      validation: {
        required: true,
        maxLength: 255,
      },
    },
    {
      name: 'smtpPort',
      label: 'SMTP Port',
      type: 'number',
      required: true,
      placeholder: '587',
      validation: {
        required: true,
        min: 1,
        max: 65535,
      },
    },
    {
      name: 'smtpSecure',
      label: 'Use SSL/TLS for SMTP',
      type: 'switch',
      switchLabel: 'Use SSL/TLS',
      description: 'Enable secure connection for SMTP',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'username',
      label: 'SMTP Username',
      type: 'text',
      required: true,
      placeholder: 'Enter SMTP username',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        required: true,
        maxLength: 255,
      },
    },
    {
      name: 'password',
      label: 'SMTP Password',
      type: 'password',
      placeholder: 'Enter SMTP password',
      autoComplete: 'off',
      ignoreAutofill: true,
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'syncEnabled',
      label: '',
      type: 'switch',
      switchLabel: 'Enable automatic email synchronization',
      description: 'Automatically sync emails from this account',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'syncInterval',
      label: 'Sync Interval (minutes)',
      type: 'number',
      placeholder: '15',
      validation: {
        min: 1,
        max: 1440,
      },
      defaultValue: 15,
    },
    {
      name: 'imapMirrorMode',
      label: 'IMAP sync scope',
      type: 'select',
      options: [
        { value: 'single_folder', label: 'INBOX only (legacy)' },
        { value: 'full_mailbox', label: 'Full mailbox (all IMAP folders)' },
      ],
      defaultValue: 'single_folder',
      helperText:
        'Full mailbox syncs every selectable folder returned by the server (LIST). Use Refresh folder list in account detail after switching.',
    },
    {
      name: 'showInAccountsSidebar',
      label: '',
      type: 'switch',
      switchLabel: 'Show in Accounts sidebar (default for shared users)',
      description: 'When shared, users see this mailbox under Accounts in /emails. Turn off for search-folder-only access.',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'showSearchFolderInbox',
      label: '',
      type: 'switch',
      switchLabel: 'Create smart folder inbox',
      description: 'Adds an INBOX entry under Smart Folders (auto-created on save). Share that folder with users who should not see Accounts.',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: '_sentFolderIdentitiesUi',
      label: 'Sent / My sent — From addresses',
      type: 'custom',
      helperText:
        'Choose which send-as addresses appear in Sent and My sent. All checked = no filter.',
      customRender: ({ formData }) => {
        const accountId = typeof formData?.id === 'number' ? formData.id : 0;
        if (accountId <= 0) {
          return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Save the account first, then configure which From addresses appear in Sent.
            </p>
          );
        }
        const savedIds = formData?.sentFolderIdentityIds;
        return (
          <SentFolderIdentitiesSection
            accountId={accountId}
            accountEmail={String(formData?.emailAddress ?? '')}
            sentFolderIdentityIds={Array.isArray(savedIds) ? savedIds : null}
            onMutate={() => {
              void swrMutate(`/email-accounts/${accountId}`);
            }}
          />
        );
      },
    },
    {
      name: '_oauthConnectUi',
      label: '',
      type: 'custom',
      customRender: ({ formData }) => {
        const accountId = typeof formData?.id === 'number' ? formData.id : 0;
        if (accountId <= 0) {
          return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Save the account first, then connect Google or Microsoft OAuth.
            </p>
          );
        }
        return (
          <OAuthConnectSection
            variant="embedded"
            accountId={accountId}
            oauthProvider={
              typeof formData?.oauthProvider === 'string' ? formData.oauthProvider : null
            }
            onConnected={() => {
              void swrMutate(`/email-accounts/${accountId}`);
            }}
          />
        );
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        description: 'Choose incoming mail: IMAP, OAuth2 (Google/Microsoft), or POP3.',
        icon: '📧',
        gridCols: 'md:grid-cols-12',
        fields: ['incomingProtocol', 'emailAddress', 'displayName'],
        fieldSpans: {
          incomingProtocol: 'md:col-span-12',
          emailAddress: 'md:col-span-6',
          displayName: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'oauth',
        title: 'OAuth2 sign-in',
        description:
          'Connect Google or Microsoft after saving the account. For Microsoft, set servers to imap.office365.com and smtp.office365.com below.',
        icon: '🔐',
        gridCols: 'md:grid-cols-1',
        fields: ['_oauthConnectUi'],
        collapsible: true,
        visibleWhen: (formData) => isOAuth2FormProtocol(formData),
      },
      {
        id: 'mail-servers-oauth',
        title: 'Mail servers (OAuth2)',
        description: 'IMAP/SMTP hosts used with XOAUTH2. Google defaults are pre-filled; adjust for Microsoft if needed.',
        icon: '📥',
        gridCols: 'md:grid-cols-12',
        fields: [
          'imapHost',
          'imapPort',
          'imapSecure',
          'imapMirrorMode',
          'smtpHost',
          'smtpPort',
          'smtpSecure',
          'username',
        ],
        fieldSpans: {
          imapHost: 'md:col-span-6',
          imapPort: 'md:col-span-3',
          imapSecure: 'md:col-span-3',
          imapMirrorMode: 'md:col-span-12',
          smtpHost: 'md:col-span-6',
          smtpPort: 'md:col-span-3',
          smtpSecure: 'md:col-span-3',
          username: 'md:col-span-6',
        },
        collapsible: true,
        visibleWhen: (formData) => isOAuth2FormProtocol(formData),
      },
      {
        id: 'imap',
        title: 'IMAP Settings',
        icon: '📥',
        gridCols: 'md:grid-cols-12',
        fields: ['imapHost', 'imapPort', 'imapSecure', 'imapUsername', 'imapPassword', 'imapMirrorMode'],
        fieldSpans: {
          imapHost: 'md:col-span-6',
          imapPort: 'md:col-span-3',
          imapSecure: 'md:col-span-3',
          imapUsername: 'md:col-span-6',
          imapPassword: 'md:col-span-6',
          imapMirrorMode: 'md:col-span-12',
        },
        collapsible: true,
        visibleWhen: (formData) => isImapFormProtocol(formData),
      },
      {
        id: 'pop3',
        title: 'POP3 Settings',
        icon: '📥',
        gridCols: 'md:grid-cols-12',
        fields: [
          'pop3Host',
          'pop3Port',
          'pop3Secure',
          'pop3Username',
          'pop3Password',
          'pop3DeleteAfterDownload',
          'pop3RetentionDays',
        ],
        fieldSpans: {
          pop3Host: 'md:col-span-6',
          pop3Port: 'md:col-span-3',
          pop3Secure: 'md:col-span-3',
          pop3Username: 'md:col-span-6',
          pop3Password: 'md:col-span-6',
          pop3DeleteAfterDownload: 'md:col-span-6',
          pop3RetentionDays: 'md:col-span-6',
        },
        collapsible: true,
        visibleWhen: (formData) => isPop3FormProtocol(formData),
      },
      {
        id: 'smtp',
        title: 'SMTP Settings',
        icon: '📤',
        gridCols: 'md:grid-cols-12',
        fields: ['smtpHost', 'smtpPort', 'smtpSecure', 'username', 'password'],
        fieldSpans: {
          smtpHost: 'md:col-span-6',
          smtpPort: 'md:col-span-3',
          smtpSecure: 'md:col-span-3',
          username: 'md:col-span-6',
          password: 'md:col-span-6',
        },
        collapsible: true,
        visibleWhen: (formData) => !isOAuth2FormProtocol(formData),
      },
      {
        id: 'sync',
        title: 'Sync Settings',
        icon: '🔄',
        gridCols: 'md:grid-cols-2',
        fields: ['syncEnabled', 'syncInterval'],
        collapsible: true,
      },
      {
        id: 'emails-sidebar',
        title: 'Emails Sidebar',
        description: 'Defaults for how this account appears on /emails. Per-user overrides are set when sharing.',
        icon: '📬',
        gridCols: 'md:grid-cols-1',
        fields: ['showInAccountsSidebar', 'showSearchFolderInbox', '_sentFolderIdentitiesUi'],
        collapsible: true,
      },
    ],
  } as FormLayout,

  // Form Configuration
  form: {
    new: {
      title: 'New Email Account',
      subtitle: 'Add a new email account configuration',
      submitText: 'Create Email Account',
    },
    edit: {
      title: 'Edit Email Account',
      subtitle: 'Update email account configuration',
      submitText: 'Update Email Account',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading email account...',
    errorMessage: 'Failed to save email account. Please try again.',
    create: async (data: EmailAccountPayloadInput): Promise<EmailAccount> => {
      const formData = prepareEmailAccountPayload(data);
      const response = await api.post('/email-accounts', formData);
      return response.data;
    },
    update: async (id: number, data: EmailAccountPayloadInput): Promise<EmailAccount> => {
      const formData = prepareEmailAccountPayload(data);
      const response = await api.patch(`/email-accounts/${id}`, formData);
      return response.data;
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Email Account Details',
    notFoundTitle: 'Email account not found',
    notFoundMessage: 'The email account you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Email Accounts',
    defaultTitle: 'Email Account',
    getTitle: (item: EmailAccount) => item.emailAddress || 'Email Account',
  },

  table: {
    actionsPosition: 'right' as const,
    onRowClick: (item: EmailAccount) => {
      window.location.href = `/email-accounts/${item.id}`;
    },
    keyExtractor: (item: EmailAccount) => item.id,
  },
  emptyStates: {
    noData: 'No email accounts found. Get started by creating your first email account.',
    noSearchResults: 'No email accounts found matching your search.',
    noFilterResults: 'No email accounts match the selected filters.',
  },
  delete: {
    title: 'Delete Email Account',
    message: 'Are you sure you want to delete this email account? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete email account. Please try again.',
    handler: async (id: number) => {
      await api.delete(`/email-accounts/${id}`);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
  colors?: ThemeColors
): Column<EmailAccount>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const textSecondary = colors?.textSecondary || 'text-gray-700';
  
  return [
  {
    key: 'emailAddress',
    header: 'Email Address',
    sortable: true,
    render: (value, row) => (
      <div className="flex items-center">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mr-3">
          <span className="text-white font-semibold text-sm">
            {row.emailAddress?.[0]?.toUpperCase() || 'E'}
          </span>
        </div>
        <div>
          <div className={`text-sm font-medium ${textColor}`}>
            <TruncatedText
              text={row.emailAddress || 'No Email'}
              maxLength={50}
              showTooltip={true}
            />
          </div>
          {row.displayName && (
            <div className={`text-sm ${textSecondary}`}>
              <TruncatedText
                text={row.displayName}
                maxLength={50}
                showTooltip={true}
              />
            </div>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'syncStatus',
    header: 'Sync Status',
    sortable: true,
    render: (value, row) => (
      <div className="flex items-center space-x-2 flex-nowrap">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.syncStatus === 'syncing'
              ? 'bg-yellow-100 text-yellow-800'
              : row.syncStatus === 'error'
              ? 'bg-red-100 text-red-800'
              : 'bg-green-100 text-green-800'
          }`}
        >
          {row.syncStatus === 'syncing'
            ? 'Syncing...'
            : row.syncStatus === 'error'
            ? 'Error'
            : 'Idle'}
        </span>
        {row.syncEnabled && (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            Auto
          </span>
        )}
      </div>
    ),
  },
  {
    key: 'lastSyncAt',
    header: 'Last Sync',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'emailCount',
    header: 'Emails',
    sortable: false,
    render: (value, row) => (
      <span className={`text-sm ${textColor}`}>{row._count?.emails || 0}</span>
    ),
  },
  {
    key: 'sendIdentityCount',
    header: 'Identities',
    sortable: false,
    render: (value, row) => {
      const count = row._count?.sendIdentities;
      if (typeof count !== 'number') {
        return <span className={`text-xs ${textSecondary}`}>-</span>;
      }
      return count === 0 ? (
        <span
          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-900"
          title="No send identities configured"
        >
          None
        </span>
      ) : (
        <span
          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800"
          title={`${count} send identit${count === 1 ? 'y' : 'ies'} configured`}
        >
          {count}
        </span>
      );
    },
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (value) => formatDate(value),
  },
];
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: EmailAccount,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.id)}
        className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      <ShareAccountButton account={item} />
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.id)}
          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

const protocolBadgeClass =
  'inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold ring-1 ring-inset';

// Helper function to get detail sections
export const getDetailSections = (
  item: EmailAccount,
  formatDate: (dateString?: string) => string
) => {
  const incomingProtocol = item.oauthProvider
    ? 'oauth2'
    : item.incomingProtocol === 'pop3'
      ? 'pop3'
      : 'imap';

  const incomingSection =
    incomingProtocol === 'oauth2'
      ? {
          title: 'OAuth2',
          fields: [
            {
              label: 'Provider',
              value: item.oauthProvider ? (
                <span className="capitalize">{item.oauthProvider}</span>
              ) : (
                'Not connected — connect on edit page'
              ),
            },
            {
              label: 'IMAP host',
              value: item.imapHost || '-',
            },
            {
              label: 'SMTP host',
              value: item.smtpHost || '-',
            },
          ],
        }
      : incomingProtocol === 'imap'
      ? {
          title: 'IMAP Settings',
          fields: [
            {
              label: 'Host',
              value: item.imapHost || '-',
            },
            {
              label: 'Port',
              value: item.imapPort?.toString() || '-',
            },
            {
              label: 'Secure Connection',
              value: item.imapSecure ? 'Yes (SSL/TLS)' : 'No',
            },
          ],
        }
      : {
          title: 'POP3 Settings',
          fields: [
            {
              label: 'Host',
              value: item.pop3Host || '-',
            },
            {
              label: 'Port',
              value: item.pop3Port != null ? String(item.pop3Port) : '-',
            },
            {
              label: 'Secure Connection',
              value:
                item.pop3Secure === true
                  ? 'Yes (SSL/TLS)'
                  : item.pop3Secure === false
                    ? 'No'
                    : '-',
            },
            {
              label: 'Delete from server after download',
              value: item.pop3DeleteAfterDownload ? 'Yes' : 'No',
            },
            {
              label: 'Retention on server (days)',
              value:
                item.pop3RetentionDays != null ? String(item.pop3RetentionDays) : '-',
              condition: item.pop3RetentionDays != null,
            },
          ],
        };

  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Email Address',
          value: item.emailAddress ? (
            <a href={`mailto:${item.emailAddress}`} className="text-indigo-600 hover:text-indigo-700">
              {item.emailAddress}
            </a>
          ) : (
            '-'
          ),
        },
        {
          label: 'Display Name',
          value: item.displayName || '-',
          condition: !!item.displayName,
        },
        {
          label: 'Incoming protocol',
          value: (
            <span
              className={`${protocolBadgeClass} ${
                incomingProtocol === 'imap'
                  ? 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-800'
                  : incomingProtocol === 'oauth2'
                    ? 'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800'
                    : 'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800'
              }`}
              title={
                incomingProtocol === 'imap'
                  ? 'Messages synced via IMAP'
                  : incomingProtocol === 'oauth2'
                    ? 'Messages synced via IMAP with OAuth2 (XOAUTH2)'
                    : 'Messages downloaded via POP3'
              }
            >
              {incomingProtocol === 'imap'
                ? 'IMAP'
                : incomingProtocol === 'oauth2'
                  ? 'OAuth2'
                  : 'POP3'}
            </span>
          ),
        },
        {
          label: 'Outgoing mail',
          value: (
            <span
              className={`${protocolBadgeClass} bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800`}
              title="Sending uses SMTP"
            >
              SMTP
            </span>
          ),
        },
        {
          label: 'Username',
          value: item.username || '-',
        },
      ],
    },
    incomingSection,
    {
      title: 'SMTP Settings',
      fields: [
        {
          label: 'Host',
          value: item.smtpHost || '-',
        },
        {
          label: 'Port',
          value: item.smtpPort?.toString() || '-',
        },
        {
          label: 'Secure Connection',
          value: item.smtpSecure ? 'Yes (SSL/TLS)' : 'No',
        },
      ],
    },
    {
      title: 'Sync Status',
      fields: [
        {
          label: 'Status',
          value: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                item.syncStatus === 'syncing'
                  ? 'bg-yellow-100 text-yellow-800'
                  : item.syncStatus === 'error'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {item.syncStatus || 'idle'}
            </span>
          ),
        },
        {
          label: 'Last Sync',
          value: item.lastSyncAt ? formatDate(item.lastSyncAt) : 'Never',
        },
        {
          label: 'Sync Enabled',
          value: item.syncEnabled ? 'Yes' : 'No',
        },
        {
          label: 'Sync Interval',
          value: item.syncInterval ? `${item.syncInterval} minutes` : '-',
        },
        {
          label: 'Total Emails',
          value: (item._count?.emails || 0).toString(),
        },
        {
          label: 'Last Error',
          value: item.syncError || '-',
          condition: !!item.syncError,
        },
      ],
    },
    {
      title: 'Additional Information',
      fields: [
        {
          label: 'Created At',
          value: formatDate(item.createdAt),
        },
        {
          label: 'Last Updated',
          value: formatDate(item.updatedAt),
          condition: !!item.updatedAt,
        },
        {
          label: 'Created By',
          value: item.createdBy?.userDisplayName || item.createdBy?.userEmail || '-',
          condition: !!item.createdBy,
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: EmailAccount) => {
  const sendIdCount = item._count?.sendIdentities;
  const sendIdentityBadge =
    typeof sendIdCount === 'number' ? (
      <span
        className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
          sendIdCount === 0
            ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-800'
            : 'bg-slate-100 text-slate-800 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-600'
        }`}
        title={
          sendIdCount === 0
            ? 'No custom From/reply identities; compose uses the account address.'
            : `${sendIdCount} configured From/reply identity${sendIdCount === 1 ? '' : 'ies'}`
        }
      >
        {sendIdCount === 0
          ? 'No send identities'
          : `${sendIdCount} send identit${sendIdCount === 1 ? 'y' : 'ies'}`}
      </span>
    ) : null;

  return (
    <>
      {sendIdentityBadge}
      <span
        className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
          item.syncStatus === 'syncing'
            ? 'bg-yellow-100 text-yellow-800'
            : item.syncStatus === 'error'
            ? 'bg-red-100 text-red-800'
            : 'bg-green-100 text-green-800'
        }`}
      >
        {item.syncStatus === 'syncing'
          ? 'Syncing...'
          : item.syncStatus === 'error'
          ? 'Error'
          : 'Idle'}
      </span>
      {item.syncEnabled && (
        <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
          Auto Sync
        </span>
      )}
    </>
  );
};

// Helper function to get initial form data
export const getInitialFormData = (): EmailAccountFormData => {
  return {
    emailAddress: '',
    displayName: '',
    incomingProtocol: 'imap',
    imapHost: '',
    imapPort: 993,
    imapSecure: true,
    pop3Host: '',
    pop3Port: 995,
    pop3Secure: true,
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: true,
    username: '',
    password: '',
    syncEnabled: true,
    syncInterval: 15,
    imapMirrorMode: 'single_folder',
    showInAccountsSidebar: true,
    showSearchFolderInbox: false,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: EmailAccount): EmailAccountFormData => {
  return {
    id: item.id,
    emailAddress: item.emailAddress || '',
    displayName: item.displayName || '',
    incomingProtocol: toFormIncomingProtocol(item),
    imapHost: item.imapHost || '',
    imapPort: item.imapPort || 993,
    imapSecure: item.imapSecure ?? true,
    pop3Host: item.pop3Host || '',
    pop3Port: item.pop3Port || 995,
    pop3Secure: item.pop3Secure ?? true,
    smtpHost: item.smtpHost || '',
    smtpPort: item.smtpPort || 587,
    smtpSecure: item.smtpSecure ?? true,
    username: item.username || '',
    password: '', // Don't populate password
    syncEnabled: item.syncEnabled ?? true,
    syncInterval: item.syncInterval || 15,
    imapMirrorMode: item.imapMirrorMode ?? 'single_folder',
    showInAccountsSidebar: item.showInAccountsSidebar ?? true,
    showSearchFolderInbox: item.showSearchFolderInbox ?? false,
    sentFolderIdentityIds: item.sentFolderIdentityIds ?? null,
    oauthProvider: item.oauthProvider ?? null,
  };
};

export function prepareEmailAccountPayload(
  data: EmailAccountPayloadInput,
): Record<string, unknown> {
  const {
    id: _id,
    oauthProvider: _oauthProvider,
    sentFolderIdentityIds: _sentFolderIdentityIds,
    sentSourceAccountIds: _sentSourceAccountIds,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    createdBy: _createdBy,
    _count: _count,
    lastSyncAt: _lastSyncAt,
    syncStatus: _syncStatus,
    syncError: _syncError,
    _sentFolderIdentitiesUi: _sentUi,
    _oauthConnectUi: _oauthUi,
    ...rest
  } = data;
  const payload: Record<string, unknown> = { ...rest };
  if (payload.incomingProtocol === 'oauth2') {
    payload.incomingProtocol = 'imap';
  } else {
    payload.incomingProtocol = toPersistedIncomingProtocol(payload.incomingProtocol);
  }
  if (payload.password === '') delete payload.password;
  return payload;
}
